// @ts-strict-ignore
import {
  Box, Icon, IconName, useSnackBar,
} from '@usealloy/component-library';
import React, { useContext, useState } from 'react';

import NodeDropdownMenu from 'flow/editor/components/Graph/nodes/BaseNode/NodeDropdownMenu';
import {
  selectNodeState,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import {
  StyledBaseNode,
  StyledNodeText,
} from 'flow/editor/components/Graph/nodes/BaseNode/BaseNode.styles';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  getDropdownItems,
  getNodeStateStyle,
} from './helpers';
import { OnIslandWorkflowRoutingNodeContainer } from './styles';
import { isPreviewWorkflowRoutingAction } from '~/dashboard/views/workflows/service/actions';
import type { BaseOnIslandNodeProps } from '../../types';
import ConfirmNodeDeletionModal from '../../../Graph/nodes/BaseNode/ConfirmNodeDeletionModal';
import { UserContext } from '~/dashboard/context';


export default function OnIslandWorkflowRoutingNode({
  action,
  allActionsOfActionType,
  isAddingNewAction = false,
  islandMode = 'expanded',
  onClick,
  onMouseEnter,
  onMouseLeave,
  onSwapOutcome,
}: BaseOnIslandNodeProps) {
  const { featureFlags } = useContext(UserContext);
  const { workflow: { name }, publicId } = action;
  const dispatch = useAppDispatch();
  const workflowProps = useAppSelector(selectWorkflowProps);
  const workflowData = useAppSelector(selectWorkflowData);

  const nodeState = useAppSelector(state => selectNodeState(state, publicId));

  const backgroundColor = 'var(--cl-gray-300)';
  const nodeStateStyle = getNodeStateStyle(nodeState);

  const addSnackBar = useSnackBar();

  const [nodeDeletionChildActionCounts, setNodeDeletionChildActionCounts] = useState<number>(0);
  const resetNodeChildActionCounts = () => setNodeDeletionChildActionCounts(0);

  const dropdownItems = React.useMemo(() => getDropdownItems({
    addSnackBar,
    dispatch,
    action,
    allActionsOfActionType,
    workflowData,
    workflowProps,
    setNodeDeletionChildActionCounts,
    onSwapOutcome,
    isWorkflowHistoryOn: Boolean(featureFlags?.workflowEditorUndoRedo),
  }), [
    addSnackBar,
    dispatch,
    action,
    allActionsOfActionType,
    workflowData,
    workflowProps,
    onSwapOutcome,
    featureFlags?.workflowEditorUndoRedo,
  ]);

  return (
    <OnIslandWorkflowRoutingNodeContainer
      onClick={event => onClick?.(event, action)}
      onMouseEnter={event => onMouseEnter(event, action)}
      onMouseLeave={event => onMouseLeave(event, action)}
      islandMode={islandMode}
    >
      {/* todo - think how to best reuse the node styles. can happen later */}
      <StyledBaseNode
        baseBackgroundColor={backgroundColor}
        nodeState={nodeState}
        style={nodeStateStyle}
      >
        <Box gap={1} alignItems="center" justifyContent="flex-start">
          <Icon icon={IconName.Workflows} />
          <StyledNodeText nodeState={nodeState}>{name}</StyledNodeText>
        </Box>
        {
          (nodeState === 'focused' || nodeState === 'focused-hovered')
          && !isPreviewWorkflowRoutingAction(action)
          && !isAddingNewAction
          && (
            <NodeDropdownMenu
              label={name}
              backgroundColor={backgroundColor}
              dropdownItems={dropdownItems}
            />
          )
        }
        {nodeDeletionChildActionCounts > 0 && (
          <ConfirmNodeDeletionModal
            nodeDeletionChildActionCounts={nodeDeletionChildActionCounts}
            resetNodeChildActionCounts={resetNodeChildActionCounts}
            id={publicId}
            isWorkflowHistoryOn={Boolean(featureFlags?.workflowEditorUndoRedo)}
          />
        )}
      </StyledBaseNode>
    </OnIslandWorkflowRoutingNodeContainer>
  );
}
